import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Banner, FormField, FormGrid, SelectionList, Tabs, Text, Tooltip, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { client as elevioClient } from 'elevio';
import { useState } from 'react';
import { Controller, useFormContext, useFormState, useWatch, } from 'react-hook-form';
import { FieldTemplate } from 'components/fieldTemplate';
import { HookFormSelectionList, HookFormText } from 'components/hookForm';
import { templateHelpText } from 'components/settings/components/sections/cards/cards.utils';
import { articles } from 'utils/elevio';
import { validateName } from 'utils/formValidator';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useNavigatePrompt } from 'utils/url/useNavigatePrompt';
import { editorMaxLines } from '../../../../configuration';
import { RequestBody } from './components/requestBody/requestBody';
import { RequestHeaders } from './components/requestHeaders/requestHeaders';
import styles from './settingsIntegrationsCardsCustomForm.module.scss';
const httpMethods = [
    'GET',
    'POST',
    'PUT',
    'PATCH',
    'DELETE',
];
const defaultAuthorizationType = 'none';
export const CustomCardsForm = ({ control, handleCustomCardSubmit, }) => {
    const [selectedTab, setSelectedTab] = useState('headers');
    const [selectedAuthorizationType, setSelectedAuthorizationType] = useState(defaultAuthorizationType);
    const isMediumScreen = useBreakpointValue({ base: true, lg: false });
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const { formState: { dirtyFields: oAuthFormDirtyFields, isSubmitting: isOAuthFormSubmitting, }, } = useFormContext();
    const { dirtyFields: customCardFormDirtyFields, isSubmitting: isCustomCardFormSubmitting, errors: customCardErrors, } = useFormState({ control });
    const isAnyFormDirty = Object.keys(Object.assign(Object.assign({}, customCardFormDirtyFields), oAuthFormDirtyFields)).length > 0;
    useNavigatePrompt(isAnyFormDirty && !isCustomCardFormSubmitting && !isOAuthFormSubmitting);
    const [requestUriTemplateFormValue, requestBodyFormValue, requestContentTypeFormValue, authorizationHeaderFormValue, customHeadersFormValue,] = useWatch({
        control,
        name: [
            'request.uri_template',
            'request.body',
            'request.content_type',
            'authorizationHeader',
            'customHeaders',
        ],
    });
    const hasUriTemplate = !!requestUriTemplateFormValue && requestUriTemplateFormValue !== '';
    const hasRequestFormData = selectedAuthorizationType !== 'none' ||
        (!!authorizationHeaderFormValue && authorizationHeaderFormValue !== '') ||
        !!customHeadersFormValue ||
        !!requestBodyFormValue ||
        !!requestContentTypeFormValue;
    const hasRequestHeadersFormError = 'authorizationHeader' in customCardErrors &&
        selectedAuthorizationType !== 'none';
    const formErrorIndicator = (_jsx(Tooltip, Object.assign({ dark: true, content: "You have form errors in this tab" }, { children: _jsx("span", { className: styles.errorIndicator }) })));
    const tabs = [
        {
            id: 'headers',
            label: 'Header',
            disabled: !hasUriTemplate,
            slotEnd: hasRequestHeadersFormError ? formErrorIndicator : false,
        },
        {
            id: 'body',
            label: 'Body',
            disabled: !hasUriTemplate,
        },
    ];
    return (_jsx("form", { children: _jsxs(FormGrid, { children: [_jsx(FormField, Object.assign({ span: isMediumScreen ? 6 : 3 }, { children: _jsx(HookFormText, { control: control, label: "Title", name: 'title', rules: {
                            required: {
                                value: true,
                                message: 'Please add a title',
                            },
                            validate: validateName,
                        }, componentProps: {
                            placeholder: 'Insert title',
                        } }) })), !isMediumScreen && _jsx(FormField, { span: 3 }), _jsx(FormField, Object.assign({ span: isSmallScreen ? 6 : 1 }, { children: _jsx(HookFormSelectionList, Object.assign({ control: control, label: "Method", name: 'request.http_method', rules: {
                            required: {
                                value: true,
                                message: 'Please select a method',
                            },
                        }, componentProps: {
                            placeholder: 'Select method',
                            getItemTitleByValue: (method) => (_jsx(Text.Paragraph, { children: method })),
                        } }, { children: httpMethods.map((method) => (_jsx(SelectionList.Item, Object.assign({ value: method }, { children: method }), method))) })) })), _jsx(FormField, Object.assign({ span: isSmallScreen ? 6 : 5 }, { children: _jsx(HookFormText, { control: control, label: "Hook URL", name: 'request.uri_template', helpIcon: {
                            dark: true,
                            title: 'Hook URL help',
                            text: (_jsxs(_Fragment, { children: ["See the", ' ', _jsx("span", Object.assign({ onClick: () => elevioClient.openArticle(articles.settings.cards.custom) }, { children: "Custom card integrations documentation" })), ' ', "for all details on how to build a hook URL with variables."] })),
                            requireClick: true,
                            tooltipClassName: styles.wideTooltip,
                        }, componentProps: {
                            placeholder: 'Insert hook URL',
                        } }) })), !hasUriTemplate && (_jsx(FormField, { children: _jsx(Banner, { variant: hasRequestFormData ? 'warning' : 'info', headline: "Request headers and body", description: hasRequestFormData ? (_jsx(_Fragment, { children: "You removed the hook URL. Doing so will also drop the authorization header, all custom headers, the body and content-type from your request." })) : (_jsx(_Fragment, { children: "Add a hook URL to let the card make a HTTP request. You will then have the option to add an authorization header, custom headers and/or a body and content-type to your request." })) }) })), _jsxs(FormField, { children: [_jsx(Tabs, { selectedId: selectedTab, tabs: tabs, onChange: (tabId) => setSelectedTab(tabId), className: styles.tabs }), _jsx(RequestHeaders, { disabled: !hasUriTemplate, control: control, className: classnames(styles.tabsChild, {
                                [styles.hidden]: selectedTab !== 'headers',
                            }), selectedAuthorizationType: selectedAuthorizationType, setSelectedAuthorizationType: setSelectedAuthorizationType, handleCustomCardSubmit: handleCustomCardSubmit }), _jsx(RequestBody, { disabled: !hasUriTemplate, control: control, className: classnames(styles.tabsChild, {
                                [styles.hidden]: selectedTab !== 'body',
                            }) })] }), _jsx(FormField, { children: _jsx(Controller, { name: 'template', control: control, rules: {
                            required: {
                                value: true,
                                message: 'Please add some template content',
                            },
                        }, render: ({ field, fieldState }) => {
                            var _a;
                            return (_jsx(FieldTemplate, { required: true, label: "Template content", input: field, placeholder: "Insert template", height: "100%", editorOptions: { maxLines: editorMaxLines }, helpIcon: {
                                    dark: true,
                                    title: 'Template help',
                                    text: templateHelpText,
                                    requireClick: true,
                                }, meta: {
                                    error: (_a = fieldState === null || fieldState === void 0 ? void 0 : fieldState.error) === null || _a === void 0 ? void 0 : _a.message,
                                } }));
                        } }) })] }) }));
};
